(function () {
  if (customElements.get("style-gallery")) return;

  function readJSON(value) {
    for (var i = 0; i < 2 && typeof value === "string"; i++) {
      try { value = JSON.parse(value); } catch (_) { return null; }
    }
    return value && typeof value === "object" ? value : null;
  }

  function plainText(value) {
    var doc = new DOMParser().parseFromString(String(value || ""), "text/html");
    doc.querySelectorAll("script, style").forEach(function (node) { node.remove(); });
    return (doc.body.textContent || "").trim();
  }

  function imageURL(value) {
    if (!value) return "";
    try {
      var url = new URL(String(value || ""), window.location.origin);
      return url.protocol === "https:" ? url.href : "";
    } catch (_) { return ""; }
  }

  function thumbnail(value) {
    var url = new URL(value);
    if (url.hostname === "review-images.judgeme.com") url.searchParams.set("width", "640");
    if (url.hostname === "judgeme.imgix.net") url.searchParams.set("w", "640");
    return url.href;
  }

  function reviewTime(value) {
    var time = Date.parse(String(value || "").replace(" UTC", "Z").replace(" ", "T"));
    return Number.isFinite(time) ? time : 0;
  }

  function makeNode(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  customElements.define("style-gallery", class StyleGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dialog = this.querySelector("dialog");
      this.grid = this.querySelector("[data-gallery-grid]");
      this.pageSize = Number(this.dataset.pageSize) || 12;
      this.items = [];
      this.filtered = [];
      this.loading = false;
      this.visibleCount = this.pageSize;
      this.bindEvents();
      this.load();
    }

    disconnectedCallback() {
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.releaseScrollLock();
      this.controller?.abort();
      this.controller = null;
    }

    releaseScrollLock() {
      if (!this.ownsScrollLock) return;
      document.documentElement.classList.remove("kb-gallery-modal-open");
      this.ownsScrollLock = false;
    }

    bindEvents() {
      var options = { signal: this.controller.signal };
      this.querySelector("[data-gallery-search]").addEventListener("input", () => this.applyFilters(true), options);
      ["category", "color", "sort"].forEach((name) => {
        this.querySelector("[data-gallery-" + name + "]").addEventListener("change", () => this.applyFilters(true), options);
      });
      this.querySelector("[data-gallery-reset]").addEventListener("click", () => {
        ["search", "category", "color"].forEach((name) => { this.querySelector("[data-gallery-" + name + "]").value = ""; });
        this.applyFilters(true);
      }, options);
      this.querySelector("[data-gallery-more]").addEventListener("click", () => {
        var firstNewIndex = Math.min(this.visibleCount, this.filtered.length);
        this.visibleCount += this.pageSize;
        this.render();
        this.grid.querySelectorAll("button")[firstNewIndex]?.focus({ preventScroll: true });
      }, options);
      this.querySelector("[data-gallery-retry]").addEventListener("click", () => this.load(), options);
      this.querySelector("[data-gallery-close]").addEventListener("click", () => this.dialog.close(), options);
      this.querySelector("[data-gallery-prev]").addEventListener("click", () => this.showPhoto(this.activeIndex - 1), options);
      this.querySelector("[data-gallery-next]").addEventListener("click", () => this.showPhoto(this.activeIndex + 1), options);
      this.dialog.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          this.showPhoto(this.activeIndex + (event.key === "ArrowLeft" ? -1 : 1));
        }
      }, options);
      this.dialog.addEventListener("click", (event) => {
        if (event.target !== this.dialog) return;
        var rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener("close", () => {
        this.releaseScrollLock();
        var trigger = this.trigger?.isConnected ? this.trigger : Array.from(this.grid.querySelectorAll("button")).find((button) => button.dataset.galleryImage === this.activePhoto);
        if (trigger) trigger.focus({ preventScroll: true });
      }, options);
    }

    async get(url, signal, asJSON) {
      var timeout = new AbortController();
      var abort = () => timeout.abort();
      signal.addEventListener("abort", abort, { once: true });
      var timer = setTimeout(abort, 12000);
      try {
        var response = await fetch(url, { signal: timeout.signal, credentials: "same-origin", headers: { Accept: asJSON ? "application/json" : "text/html" } });
        if (!response.ok) {
          var error = new Error("Gallery source unavailable");
          error.status = response.status;
          throw error;
        }
        return await (asJSON ? response.json() : response.text());
      } finally {
        clearTimeout(timer);
        signal.removeEventListener("abort", abort);
      }
    }

    productPath(value) {
      try {
        var canonical = new URL(this.dataset.storeUrl || window.location.origin);
        var url = new URL(value, canonical);
        var normalizeHost = (host) => host.replace(/^www\./, "");
        if (normalizeHost(url.hostname) !== normalizeHost(canonical.hostname) && url.origin !== window.location.origin) return "";
        var match = url.pathname.match(/\/products\/([^/]+)\/?$/);
        if (!match) return "";
        var handle = decodeURIComponent(match[1]);
        if (!handle || /[/?#]/.test(handle)) return "";
        var root = this.dataset.rootUrl || "/";
        return root.replace(/\/?$/, "/") + "products/" + encodeURIComponent(handle);
      } catch (_) { return ""; }
    }

    collect(data) {
      var photos = new Map();
      // These are public, presentation-ready snapshots maintained by Judge.me.
      // Product reviews take precedence over gallery summaries of the same photo.
      [data.product_reviews, data.photo_gallery, data.all_reviews].forEach((reviews) => {
        if (!Array.isArray(reviews)) return;
        reviews.forEach((review) => {
          if (review.hidden === true || review.is_shop_review === true || review.product_in_store === false || (review.curated && review.curated !== "ok")) return;
          var productPath = this.productPath(review.product_url);
          if (!productPath) return;
          (Array.isArray(review.pictures_urls) ? review.pictures_urls : []).forEach((picture) => {
            if (!picture || picture.hidden === true) return;
            var full = imageURL(picture.original || picture.huge || picture.compact);
            if (!full) return;
            var key = new URL(full).origin + new URL(full).pathname;
            if (photos.has(key)) return;
            photos.set(key, {
              image: full,
              productPath: productPath,
              productTitle: plainText(review.product_title),
              productImage: imageURL(review.product_variant_image_url || review.product_pictures_url),
              variantTitle: String(review.product_variant_title || ""),
              author: review.is_anonymous_reviewer ? "Anonymous" : String(review.reviewer_name || "A KISSBRIDAL customer"),
              title: String(review.title || ""),
              body: plainText(review.body_html || review.body),
              rating: Math.round(Math.max(0, Math.min(5, Number(review.rating) || 0))),
              verified: review.verified_buyer === true,
              time: reviewTime(review.created_at),
              category: "",
              color: ""
            });
          });
        });
      });
      return Array.from(photos.values());
    }

    async load() {
      var signal = this.controller.signal;
      if (this.loading) return;
      this.loading = true;
      this.querySelector("[data-gallery-retry]").hidden = true;
      this.querySelector("[data-gallery-empty]").hidden = true;
      this.grid.setAttribute("aria-busy", "true");
      this.querySelector("[data-gallery-count]").textContent = "Loading customer photos…";
      try {
        var sourceElement = this.querySelector("[data-gallery-source]");
        var data = readJSON(sourceElement.content.textContent);
        if (!data) {
          var source = new URL(this.dataset.reviewsUrl, window.location.origin);
          if (source.origin !== window.location.origin || source.href === window.location.href) throw new Error("Invalid review source");
          var html = await this.get(source.href, signal, false);
          var doc = new DOMParser().parseFromString(html, "text/html");
          data = readJSON(doc.querySelector(".jdgm-all-reviews-widget-v2025-data-json")?.textContent);
          if (!data) throw new Error("Review data unavailable");
        }
        if (signal.aborted) return;
        this.items = this.collect(data);
        this.applyFilters(true);
        // Photos appear immediately; independent product requests enrich filters
        // and pricing without delaying the first render or reading any secrets.
        await this.enrichProducts(signal);
        if (!signal.aborted) {
          this.updateFilterOptions();
          this.applyFilters(false);
        }
      } catch (_) {
        if (signal.aborted) return;
        this.items = [];
        this.filtered = [];
        this.grid.replaceChildren();
        this.querySelector("[data-gallery-more]").hidden = true;
        this.querySelector("[data-gallery-count]").textContent = "";
        this.showEmpty("Our gallery is taking a moment", "Please try again, or explore our customer reviews below.");
        this.querySelector("[data-gallery-retry]").hidden = false;
      } finally {
        if (!signal.aborted) {
          this.loading = false;
          this.grid.setAttribute("aria-busy", "false");
        }
      }
    }

    async enrichProducts(signal) {
      var paths = Array.from(new Set(this.items.map((item) => item.productPath)));
      var index = 0;
      var worker = async () => {
        while (index < paths.length && !signal.aborted) {
          var path = paths[index++];
          try {
            var product = await this.get(path + ".js", signal, true);
            if (signal.aborted) return;
            this.items.filter((item) => item.productPath === path).forEach((item) => {
              item.productTitle = String(product.title || item.productTitle);
              item.productImage = imageURL(product.featured_image) || item.productImage;
              item.category = String(product.type || "").trim();
              var options = Array.isArray(product.options) ? product.options : [];
              var colorIndex = options.findIndex((option) => /^(color|colour)$/i.test(option.name || ""));
              var color = options[colorIndex];
              var variant = (product.variants || []).find((value) => item.variantTitle && value.title === item.variantTitle);
              if (color && variant) item.color = String((variant.options || [])[colorIndex] || "");
              else if (color && Array.isArray(color.values) && color.values.length === 1) item.color = String(color.values[0]);
              item.price = Number.isFinite(product.price) ? product.price : null;
            });
          } catch (error) {
            if (error.status === 404 && !signal.aborted) this.items = this.items.filter((item) => item.productPath !== path);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, paths.length) }, worker));
    }

    updateFilterOptions() {
      ["category", "color"].forEach((name) => {
        var select = this.querySelector("[data-gallery-" + name + "]");
        var selected = select.value;
        var values = Array.from(new Set(this.items.map((item) => item[name]).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        var first = makeNode("option", "", name === "category" ? "All looks" : "All colors");
        first.value = "";
        select.replaceChildren(first);
        values.forEach((value) => {
          var option = makeNode("option", "", value);
          option.value = value;
          select.appendChild(option);
        });
        select.value = values.includes(selected) ? selected : "";
        select.disabled = !values.length;
      });
    }

    applyFilters(reset) {
      var query = this.querySelector("[data-gallery-search]").value.trim().toLocaleLowerCase();
      var category = this.querySelector("[data-gallery-category]").value;
      var color = this.querySelector("[data-gallery-color]").value;
      var sort = this.querySelector("[data-gallery-sort]").value;
      this.filtered = this.items.filter((item) => (!category || item.category === category) && (!color || item.color === color) && (!query || [item.productTitle, item.category, item.color].join(" ").toLocaleLowerCase().includes(query)));
      this.filtered.sort((a, b) => sort === "rating" ? b.rating - a.rating || b.time - a.time : b.time - a.time);
      if (reset) this.visibleCount = this.pageSize;
      this.querySelector("[data-gallery-reset]").hidden = !query && !category && !color;
      this.render();
      if (this.dialog.open) {
        var active = this.filtered.findIndex((item) => item.image === this.activePhoto);
        if (active === -1) this.dialog.close();
        else this.showPhoto(active);
      }
    }

    render() {
      var fragment = document.createDocumentFragment();
      this.filtered.slice(0, this.visibleCount).forEach((item, index) => {
        var card = makeNode("article", "kb-gallery__card");
        var button = makeNode("button", "kb-gallery__card-button");
        button.type = "button";
        button.dataset.galleryImage = item.image;
        button.setAttribute("aria-label", "View " + item.author + "’s look: " + item.productTitle);
        button.setAttribute("aria-haspopup", "dialog");
        var image = makeNode("img");
        image.src = thumbnail(item.image);
        image.alt = "Customer wearing " + item.productTitle;
        image.width = 600;
        image.height = 800;
        image.loading = index < 4 ? "eager" : "lazy";
        image.decoding = "async";
        button.append(image, makeNode("span", "kb-gallery__card-cta", "Explore this look ↗"));
        button.addEventListener("click", () => {
          this.trigger = button;
          this.showPhoto(index);
          this.dialog.showModal();
          document.documentElement.classList.add("kb-gallery-modal-open");
          this.ownsScrollLock = true;
        });
        var product = makeNode("a", "kb-gallery__card-product", item.productTitle);
        product.href = item.productPath;
        card.append(button, makeNode("p", "kb-gallery__card-name", item.author + "’s moment"), product);
        fragment.appendChild(card);
      });
      this.grid.replaceChildren(fragment);
      var count = this.filtered.length;
      this.querySelector("[data-gallery-count]").textContent = count + (count === 1 ? " customer photo" : " customer photos");
      this.querySelector("[data-gallery-more]").hidden = this.visibleCount >= count;
      this.querySelector("[data-gallery-empty]").hidden = count > 0;
      if (!count) this.showEmpty(this.items.length ? "No looks match just yet" : "More moments are on their way", this.items.length ? "Try another color or style, or clear your filters." : "Explore our customer reviews while we gather more inspiration.");
    }

    showEmpty(title, description) {
      this.querySelector("[data-gallery-empty]").hidden = false;
      this.querySelector("[data-gallery-empty-title]").textContent = title;
      this.querySelector("[data-gallery-empty-text]").textContent = description;
    }

    showPhoto(index) {
      if (index < 0 || index >= this.filtered.length) return;
      this.activeIndex = index;
      var item = this.filtered[index];
      if (!this.dialog.open || this.activePhoto !== item.image) this.querySelector(".kb-gallery__dialog-body").scrollTop = 0;
      this.activePhoto = item.image;
      var setText = (name, value) => { this.querySelector("[data-gallery-" + name + "]").textContent = value; };
      var photo = this.querySelector("[data-gallery-photo]");
      photo.src = item.image;
      photo.alt = "Customer wearing " + item.productTitle;
      setText("title", item.title || item.productTitle);
      setText("author", item.author);
      setText("review", item.body);
      setText("rating", "★".repeat(item.rating) + "☆".repeat(5 - item.rating));
      this.querySelector("[data-gallery-rating]").setAttribute("aria-label", item.rating + " out of 5 stars");
      this.querySelector("[data-gallery-verified]").hidden = !item.verified;
      setText("position", (index + 1) + " / " + this.filtered.length);
      setText("date", item.time ? new Intl.DateTimeFormat(document.documentElement.lang || "en", { year: "numeric", month: "long", day: "numeric" }).format(item.time) : "");
      setText("product-title", item.productTitle);
      setText("product-color", item.color ? "Product color: " + item.color : "");
      this.querySelector("[data-gallery-product-color]").hidden = !item.color;
      var productImage = this.querySelector("[data-gallery-product-image]");
      productImage.hidden = !item.productImage;
      if (item.productImage) { productImage.src = item.productImage; productImage.alt = item.productTitle; }
      var price = this.querySelector("[data-gallery-product-price]");
      price.hidden = !Number.isFinite(item.price);
      if (!price.hidden) price.textContent = new Intl.NumberFormat(document.documentElement.lang || "en", { style: "currency", currency: this.dataset.currency || "USD" }).format(item.price / 100);
      this.querySelector("[data-gallery-shop]").href = item.productPath;
      this.querySelector("[data-gallery-prev]").disabled = index === 0;
      this.querySelector("[data-gallery-next]").disabled = index === this.filtered.length - 1;
    }
  });
})();
